const POCKETBASE_URL = String(
  import.meta.env.VITE_POCKETBASE_URL ||
  'https://population-coming-bpa-email-gateway-mask.pockethost.io/'
).replace(/\/$/, '');

const AUTH_COLLECTION = String(
  import.meta.env.VITE_POCKETBASE_AUTH_COLLECTION || 'users'
).trim();

const ADMIN_ENDPOINT = `${POCKETBASE_URL}/api/bpa-mail/gateway`;
const SEND_ENDPOINT = `${POCKETBASE_URL}/api/bpa-mail/send`;
const SESSION_KEY = 'bpa_admin_session';

export const getStoredSession = () => sessionStorage.getItem(SESSION_KEY) || '';

export const storeSession = (token) => {
  if (token) sessionStorage.setItem(SESSION_KEY, token);
  else sessionStorage.removeItem(SESSION_KEY);
};

const makeError = async (response, fallbackCode, fallbackMessage) => {
  let body = {};
  try {
    body = await response.json();
  } catch {
    body = {};
  }

  const error = new Error(
    body?.message ||
    body?.data?.message ||
    fallbackMessage ||
    `Request failed (${response.status})`
  );
  error.code = body?.errorCode || body?.code || fallbackCode;
  error.status = response.status;
  error.data = body?.data || {};
  return error;
};

const pbFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  if (!response.ok) {
    throw await makeError(
      response,
      'POCKETBASE_REQUEST_FAILED',
      'PocketBase request failed.'
    );
  }

  return response.json();
};

export async function gatewayRequest(action, payload = {}) {
  const token = getStoredSession();
  if (!token) {
    const error = new Error('Administrator session is required.');
    error.code = 'ADMIN_AUTH_REQUIRED';
    error.status = 401;
    throw error;
  }

  const response = await fetch(ADMIN_ENDPOINT, {
    method: 'POST',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ action, payload })
  });

  let body = {};
  try {
    body = await response.json();
  } catch {
    body = {
      success: false,
      action,
      message: 'PocketBase gateway returned an unreadable response.',
      errorCode: 'INVALID_GATEWAY_RESPONSE',
      data: {}
    };
  }

  if (!response.ok || body.success === false) {
    const edgeMessage = response.status === 404
      ? 'Mail Hub API route not found (404). Install bpa_mail_hub.pb.js and mail_service.js on the configured PocketHost instance, or confirm the backend URL.'
      : '';
    const error = new Error(edgeMessage || body.message || `Gateway request failed (${response.status})`);
    error.code = body.errorCode || body.code || 'GATEWAY_REQUEST_FAILED';
    error.status = response.status;
    error.data = body.data || {};
    throw error;
  }

  return body;
}

const health = async () => {
  let pocketbase = 'unreachable';

  try {
    const pbResponse = await fetch(`${POCKETBASE_URL}/api/health`, { cache: 'no-store' });
    if (pbResponse.ok) pocketbase = 'connected';
  } catch {
    pocketbase = 'unreachable';
  }

  try {
    const response = await fetch(`${POCKETBASE_URL}/api/bpa-mail/health`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Mail backend unavailable');
    const body = await response.json();
    return {
      ...body,
      data: {
        ...(body.data || {}),
        pocketbase: body.data?.pocketbase || pocketbase,
        runtime: 'PocketBase hooks',
        storage: 'PocketBase',
        authMode: 'pocketbase_email_password',
        authCollection: AUTH_COLLECTION,
        pocketbaseUrl: POCKETBASE_URL
      }
    };
  } catch {
    return {
      success: false,
      action: 'health_check',
      message: pocketbase === 'connected'
        ? 'PocketBase is reachable, but the Mail Hub server-side hook is missing or not loading.'
        : 'PocketBase or the Mail Hub API is unavailable.',
      errorCode: null,
      data: {
        gateway: 'unreachable',
        pocketbase,
        runtime: 'PocketBase hooks',
        storage: 'PocketBase',
        authMode: 'pocketbase_email_password',
        authCollection: AUTH_COLLECTION,
        pocketbaseUrl: POCKETBASE_URL
      }
    };
  }
}

const login = async (email, password) => {
  const body = await pbFetch(
    `${POCKETBASE_URL}/api/collections/${encodeURIComponent(AUTH_COLLECTION)}/auth-with-password`,
    {
      method: 'POST',
      body: JSON.stringify({
        identity: String(email || '').trim(),
        password: String(password || '')
      })
    }
  );

  const record = body.record || {};
  return {
    success: true,
    action: 'auth_login',
    message: 'PocketBase account authenticated.',
    errorCode: null,
    data: {
      token: body.token,
      admin: {
        id: record.id,
        role: record.role || 'owner',
        email: record.email || String(email || '').trim(),
        displayName:
          record.name ||
          record.displayName ||
          record.display_name ||
          record.email ||
          'BPA Admin'
      }
    }
  };
};

const session = async () => {
  const token = getStoredSession();
  if (!token) {
    const error = new Error('Administrator session is required.');
    error.code = 'ADMIN_AUTH_REQUIRED';
    error.status = 401;
    throw error;
  }

  const body = await pbFetch(
    `${POCKETBASE_URL}/api/collections/${encodeURIComponent(AUTH_COLLECTION)}/auth-refresh`,
    {
      method: 'POST',
      headers: {
        Authorization: token
      }
    }
  );

  const record = body.record || {};
  if (body.token) storeSession(body.token);

  return {
    success: true,
    action: 'auth_session',
    message: 'PocketBase session is valid.',
    errorCode: null,
    data: {
      authenticated: true,
      token: body.token || token,
      admin: {
        id: record.id,
        role: record.role || 'owner',
        email: record.email || '',
        displayName:
          record.name ||
          record.displayName ||
          record.display_name ||
          record.email ||
          'BPA Admin'
      }
    }
  };
};

export const gatewayApi = {
  health,
  login,
  session,
  checkAdmin: () => gatewayRequest('admin_whoami'),
  logout: () => {
    storeSession('');
    return Promise.resolve();
  },

  ensureSchema: () => gatewayRequest('ensure_phase2_schema'),

  listVault: (page = 1) => gatewayRequest('vault_list', { page }),
  revealVaultKey: (keyId,password) => gatewayRequest('vault_reveal', { keyId,password }),
  importVaultKey: (keyId,password,rawKey) => gatewayRequest('vault_import', { keyId,password,rawKey }),
  auditVaultCopy: (keyId) => gatewayRequest('vault_copy_event', { keyId }),
  listClients: () => gatewayRequest('list_clients'),
  listClientsPage: (page = 1) => gatewayRequest('list_clients_page', { page }),
  listAppsPage: (page = 1) => gatewayRequest('list_apps_page', { page }),
  listApiKeysPage: (page = 1) => gatewayRequest('list_api_keys_page', { page }),
  onboardBusiness: (payload) => gatewayRequest('onboard_business', payload),
  quickConnectPreflight: () => gatewayRequest('quick_connect_preflight'),
  quickConnectApp: (payload) => gatewayRequest('quick_connect_app', payload),
  onboardExistingApp: (payload) => gatewayRequest('onboard_existing_app', payload),
  diagnoseOnboarding: (appId, testRecipient) => gatewayRequest('diagnose_onboarding', { appId, testRecipient }),
  listWorkflowRoutes: (appId) => gatewayRequest('list_workflow_routes',{appId}),
  saveWorkflowRoute: (payload) => gatewayRequest('save_workflow_route',payload),
  getTenantProvisioning: (appId) => gatewayRequest('get_tenant_provisioning', { appId }),
  setTenantProvisioning: (payload) => gatewayRequest('set_tenant_provisioning', payload),
  createClient: (payload) => gatewayRequest('create_client', payload),
  updateClient: (payload) => gatewayRequest('update_client', payload),
  clientOverview: (id) => gatewayRequest('client_overview', { id }),

  listPublicForms: () => gatewayRequest('list_public_forms'),
  savePublicForm: (payload) => gatewayRequest('save_public_form', payload),
  listApps: () => gatewayRequest('list_apps'),
  getAssessmentDeliveryConfig: (appId) => gatewayRequest('get_assessment_delivery_config',{appId}),
  saveAssessmentDeliveryConfig: (payload) => gatewayRequest('save_assessment_delivery_config',payload),
  createApp: (payload) => gatewayRequest('create_app', payload),
  updateApp: (payload) => gatewayRequest('update_app', payload),

  listApiKeys: () => gatewayRequest('list_api_keys'),
  generateApiKey: (payload) => gatewayRequest('generate_api_key', payload),
  updateApiKey: (payload) => gatewayRequest('update_api_key', payload),
  setApiKeyStatus: (id, status) => gatewayRequest('set_api_key_status', { id, status }),
  rotateApiKey: (id) => gatewayRequest('rotate_api_key', { id }),
  revokeApiKey: (id) => gatewayRequest('revoke_api_key', { id }),

  dashboardSummary: () => gatewayRequest('dashboard_summary'),
  usageSummary: () => gatewayRequest('usage_summary'),
  listEmailLogs: (limit = 100) => gatewayRequest('list_email_logs', { limit }),
  listAuditLogs: (limit = 100) => gatewayRequest('list_audit_logs', { limit }),

  listProviders: () => gatewayRequest('list_providers'),
  updateProvider: (payload) => gatewayRequest('update_provider', payload),
  testProvider: (to, appId, sendNow = false) => gatewayRequest('provider_test', { to, appId, sendNow }),

  listEmailPacks: () => gatewayRequest('list_email_packs'),
  createEmailPack: (payload) => gatewayRequest('create_email_pack', payload),
  updateEmailPack: (payload) => gatewayRequest('update_email_pack', payload),
  deleteEmailPack: (id) => gatewayRequest('delete_email_pack', { id }),

  listSettings: () => gatewayRequest('list_settings'),
  listTemplates: () => gatewayRequest('list_templates'),
  listTemplatesPage: (page = 1) => gatewayRequest('list_templates_page', { page }),
  saveTemplate: (payload) => gatewayRequest('save_template', payload),
  listSchedules: () => gatewayRequest('list_scheduled_emails'),
  createSchedule: (payload) => gatewayRequest('create_schedule', payload),
  updateSchedule: (payload) => gatewayRequest('update_schedule', payload),
  listQueue: () => gatewayRequest('list_queue'),
  retryQueue: (id) => gatewayRequest('retry_queue', { id }),
  cancelQueue: (id) => gatewayRequest('cancel_queue', { id }),
  listSuppressions: () => gatewayRequest('list_suppressions'),
  createSuppression: (payload) => gatewayRequest('create_suppression', payload),
  deleteSuppression: (id) => gatewayRequest('delete_suppression', { id }),
  saveSetting: (payload) => gatewayRequest('save_setting', payload)
};

export async function sendClientEmail(apiKey, payload) {
  const response = await fetch(SEND_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-BPA-Key': apiKey
    },
    body: JSON.stringify(payload)
  });

  let body = {};
  try {
    body = await response.json();
  } catch {
    body = {
      success: false,
      message: 'PocketBase gateway returned an unreadable response.',
      errorCode: 'INVALID_GATEWAY_RESPONSE'
    };
  }

  if (!response.ok || body.success === false) {
    const error = new Error(body.message || 'Email request failed.');
    error.code = body.errorCode || body.code || 'SEND_FAILED';
    error.status = response.status;
    error.data = body.data || {};
    throw error;
  }

  return body;
}

export const pocketBaseConfig = {
  url: POCKETBASE_URL,
  authCollection: AUTH_COLLECTION,
  adminEndpoint: ADMIN_ENDPOINT,
  sendEndpoint: SEND_ENDPOINT
};